import type {TaxonomyEditorOptions} from "../TaxonomyEditorOptions";
import {DEBUG_LIMIT_DATA, IS_TE_DEVELOPMENT_MODE} from "../TaxonomyEditorOptions";
import type {Category} from "../../../../stores/TaxonomyEditorStore";
import TaxonomyEditorStore from "../../../../stores/TaxonomyEditorStore";
import type TaxonomyManagerStore from "../../../../stores/TaxonomyManagerStore";
import {m_taxonomy} from "../../../../services/classes/TaxonomyClasses";
import {TaxonomyRenderer} from "./TaxonomyRenderer";
import {taxonomy_editor} from "../TaxonomyEditorTypes";
import type {IReactionDisposer} from "mobx";
import {reaction} from "mobx";
import * as d3 from "d3";
import {UNCATEGORIZED_LABEL} from "../../../../constants";
import type {HierarchyNode} from "d3-hierarchy";
import type {Writeable} from "../../../../utils/ts-utils";
import {sum} from "../../../../utils/js-utils";
import type AuthStore from "../../../../authentication/AuthStore";
import type {TaxonomyNodeValueMode} from "./TaxonomyTreeValueModes";
import {assignValues} from "./TaxonomyTreeValueModes";

type Node = taxonomy_editor.GraphNode;

/**
 * For controlling the editing operations of the taxonomy
 * This controller combine the React, Mobx and d3.js frameworks
 */
export class TaxonomyTreeEditorController {
    private renderer: TaxonomyRenderer | null = null
    private reactions: IReactionDisposer[] = []
    public initialized = false;

    constructor(
        public wrapperRef: HTMLDivElement,
        public options: TaxonomyEditorOptions,
        private taxonomyEditorStore: TaxonomyEditorStore,
        private taxonomyManagerStore: TaxonomyManagerStore,
        private authStore: AuthStore,
    ) {
    }

    public initializeData(
        data: m_taxonomy.FullSerializer,
    ) {
        const d = TaxonomyTreeEditorController.preProcessData(data, this.taxonomyEditorStore.valueMode);
        this.renderer = new TaxonomyRenderer(this, d)

        this.reactions = [
            reaction(
                () => this.taxonomyManagerStore.historyState,
                () => this.renderer!.setHistoryClasses(this.taxonomyManagerStore.historyState)
            ),
            reaction(
                () => this.taxonomyEditorStore.builderMode,
                (curr, prev) => {
                    const prevIsEdit = prev === 'edit';
                    const currIsEdit = curr === 'edit';

                    const prevIsFull = TaxonomyEditorStore.isFullViewMode(prev);
                    const currIsFull = TaxonomyEditorStore.isFullViewMode(curr)

                    const changeEdit = currIsEdit !== prevIsEdit;
                    const expand = !prevIsFull && currIsFull ? 'expand' : prevIsFull && !currIsFull ? 'shrink' : 'keep';

                    this.onChangeMode(changeEdit, expand)
                }
            ),
        ]
        this.taxonomyEditorStore.registerController(this)

        this.initialized = true;
    }

    public redraw(data: m_taxonomy.FullSerializer) {
        console.log('[TaxonomyTreeEditorController.redraw] start')
        const d = TaxonomyTreeEditorController.preProcessData(data, this.taxonomyEditorStore.valueMode);
        const prevFocusId = this.taxonomyEditorStore.focus?.data.id ?? null;
        const newFocus = this.renderer!.drawNewHierarchy(d, prevFocusId);
        this.taxonomyEditorStore.setFocus(newFocus)
        console.log('[TaxonomyTreeEditorController.redraw] end')
    }

    private static preProcessData(data: taxonomy_editor.InputData, valueMode: TaxonomyNodeValueMode): HierarchyNode<taxonomy_editor.NodeData> {
        // TODO: Add sorting of the tree
        const tree = data.diff_tree
        if (!tree) throw Error('`diff_tree` not found in taxonomy data')
        const rootData: taxonomy_editor.NodeData = TaxonomyTreeEditorController.extractNodeData(tree);
        const rootHierarchy: HierarchyNode<taxonomy_editor.NodeData> = d3.hierarchy(rootData)
        assignValues(valueMode, rootHierarchy);

        if (IS_TE_DEVELOPMENT_MODE) {
            TaxonomyTreeEditorController.checkDuplicates(rootHierarchy)
        }

        return rootHierarchy
    }

    public static checkDuplicates(root: HierarchyNode<taxonomy_editor.NodeData>) {
        // Count the ids
        const idMap = new Map<number, taxonomy_editor.NodeData[]>()
        root.descendants().forEach(n => {
            const id = n.data.id;
            const matches = idMap.get(id) || []
            matches.push(n.data)
            idMap.set(id, matches)
        })
        const duplicates = Array.from(idMap.entries()).filter(([_, matches]) => matches.length > 1)
        if (duplicates.length > 0) {
            console.error('Found duplicate ids in the data of the taxonomy builder: ',
                duplicates.map(([id, count]) => `id=${id} (${count.map(c => `${c.label}`)})`)
            )
        } else {
            console.log(`No duplicates found in ${idMap.size} ids`)
        }
    }

    private static extractNodeData(inNode: m_taxonomy.Tree, depth = 1): taxonomy_editor.NodeData {
        let inChildren = inNode.children;
        if (DEBUG_LIMIT_DATA) {
            if (depth >= 3) {
                inChildren = []
            } else {
                inChildren = inChildren.filter((_, i) => i < 3)
            }
        }
        // Remove uncategorized
        inChildren = inChildren.filter(c => c.label !== UNCATEGORIZED_LABEL)

        const outNode: taxonomy_editor.NodeData = TaxonomyTreeEditorController.extractSingleNodeData(inNode);
        outNode.children.push(...inChildren.map<taxonomy_editor.NodeData>(c =>
            TaxonomyTreeEditorController.extractNodeData(c, depth + 1)
        ))
        return outNode
    }

    private static extractSingleNodeData(inputNode: m_taxonomy.Tree): taxonomy_editor.NodeData {
        let values: taxonomy_editor.Values
        const isLeaf = inputNode.children.length === 0
        if (isLeaf) {
            values = {...inputNode.values}
        } else {
            values = taxonomy_editor.extractIntermediateValues(inputNode.values)
        }
        return taxonomy_editor.buildNode({
            id: inputNode.id,
            label: inputNode.label,
            values,
            sources: inputNode.sources,
            removed: inputNode.removed,
        }, inputNode.values?.authors || [])
    }

    private createNewNode(data: taxonomy_editor.AddNodeData): taxonomy_editor.NodeData {
        const authors = [this.authStore.getUserId()];
        return taxonomy_editor.buildNode({
            id: data.newId,
            label: data.label,
            values: taxonomy_editor.NO_VALUES(data.description, authors),
            sources: [],
            removed: false,
        }, authors);
    }

    private createMergedNode(data: taxonomy_editor.MergeNodeData): taxonomy_editor.NodeData {

        // Combine authors
        const authorsSet = new Set(data.sources.flatMap(n => n.data.values.authors || []));
        authorsSet.add(this.authStore.getUserId());
        const authors = Array.from(authorsSet);

        return taxonomy_editor.buildNode({
            id: data.destination.newId,
            label: data.destination.label,
            values: taxonomy_editor.mergeValues(
                data.sources.map(n => n.data.values),
                '',
            ),
            sources: data.sources.flatMap(n => n.data.sources),
            removed: false,
        }, authors);
    }

    get builderMode() {
        return this.taxonomyEditorStore.builderMode;
    }

    get isEditMode() {
        return this.taxonomyEditorStore.isEditMode
    }

    get focusLevel() {
        return this.taxonomyEditorStore.focusLevel
    }

    /**
     * TODO: Simplify the selection management.
     * Where is it called, what does it update?
     */
    doDrawSelection() {
        if (!this.renderer) {
            console.warn('Cannot draw the selection')
        } else {
            this.renderer.drawSelection();
        }
    }

    backgroundClicked() {
        this.resetSelection();
    }

    onClickBar(n: Node) {
        console.log('[CONTROLLER] clicked bar', n.data.id)

        const prevFocus = this.taxonomyEditorStore.focus
        const newFocus = TaxonomyTreeEditorController.focusNavClick(n, prevFocus)
        this.changeFocus(prevFocus, newFocus)
    }

    onDoubleClickLabel(n: Node) {
        console.log('[CONTROLLER] double clicked label', n.data.id)
        if (this.taxonomyEditorStore.isEditMode) {
            this.taxonomyEditorStore.setSelected(n, true)
            this.doDrawSelection()
            if (this.taxonomyEditorStore.selection.length === 1) {
                this.taxonomyEditorStore.setUpdateCategoryMode(true)
            }
        }
    }

    onClickLabel(n: Node) {
        console.log('[CONTROLLER] clicked label', n.data.id)
        console.log('[CONTROLLER] clicked label - n: ', n.y0, n.y1)
        console.log('[CONTROLLER] clicked label - n.data.target: ', n.data.target.y0, n.data.target.y1)

        if (!this.taxonomyEditorStore.isEditMode) {
            const prevFocus = this.taxonomyEditorStore.focus
            const newFocus = TaxonomyTreeEditorController.focusNavClick(n, prevFocus)
            this.changeFocus(prevFocus, newFocus)
        } else {
            this.taxonomyEditorStore.toggleSelection(n)
            this.doDrawSelection()
        }
    }

    moveFocusUp() {
        const prevFocus = this.taxonomyEditorStore.focus;
        console.log('Go up one level', prevFocus?.depth)
        if (prevFocus && prevFocus.parent) {
            const newFocus = prevFocus.parent
            this.changeFocus(prevFocus, newFocus)
        }
    }

    public changeFocusAndSetSelection(nodeId: number) {
        const node = this.findNodeById(nodeId)
        if (!node || !node.parent) {
            console.warn('Cannot navigate to node, not found', nodeId)
            return false;
        }
        const newFocus = this.findNodeById(node.parent.data.id);
        if (!newFocus) {
            console.warn('Cannot navigate to node parent, not found', nodeId)
            return false;
        }
        const prevFocus = this.taxonomyEditorStore.focus;

        this.taxonomyEditorStore.setFocus(newFocus)
        this.taxonomyEditorStore.selectionIds.replace([nodeId]);
        this.renderer!.setFocusClass(newFocus)

        const factor = TaxonomyRenderer.estDurationFactor(prevFocus, newFocus)
        this.renderer!.animateTarget(newFocus, factor)
        this.doDrawSelection()

        return true;
    }

    private changeFocus(prevFocus: Node | null, newFocus: Node) {
        const _newFocus = this.findNodeById(newFocus.data.id);
        if (!_newFocus) {
            console.error('Could not find the new focus')
            return;
        }
        newFocus = _newFocus;

        this.taxonomyEditorStore.setFocusAndClearSelection(newFocus)
        this.doDrawSelection()
        this.renderer!.setFocusClass(newFocus)

        const factor = TaxonomyRenderer.estDurationFactor(prevFocus, newFocus)
        this.renderer!.animateTarget(newFocus, factor)
    }

    /**
     * This function should properly animate the targets as well (WIP)
     */
    public reDrawV2(structureChanged = false) {
        const prevFocus = this.taxonomyEditorStore.focus;
        console.log('[TaxonomyRenderer] reDraw2', prevFocus?.data.id)

        if (structureChanged) {
            //  <<<<< DO OR DO NOT ????
            this.renderer!.drawView()
        }
        this.renderer!.resetLayout();

        const newFocus = this.findFocus()
        this.taxonomyEditorStore.overwriteFocus(newFocus)

        const targetFocus = newFocus || this.renderer!.root;
        const factor = TaxonomyRenderer.estDurationFactor(prevFocus, targetFocus)
        this.renderer!.animateTarget(targetFocus, factor)
    }

    public onChangeMode(changeEdit: boolean, changeView: 'expand' | 'shrink' | 'keep') {
        let p: Promise<void>;
        if (changeView !== 'keep') {
            p = this.changeViewMode()
        } else {
            p = Promise.resolve();
        }

        if (changeEdit) {
            p = p.then(() => this.changeEditMode())
        }
        p.then(() => this.renderer!.setWrapperClasses(this.taxonomyEditorStore.builderMode))

    }

    private changeEditMode(): Promise<void> {
        return this.renderer!.onChangeEditMode().end();
    }

    private changeViewMode(): Promise<void> {
        const editMode = this.taxonomyEditorStore.isEditMode;
        const fitViewMode = !this.taxonomyEditorStore.isFullViewMode;
        if (fitViewMode) {
            // The visualization has been shrunk
            console.log('[CONTROLLER] changeViewMode() -> shrink')

            const svgHeight = this.renderer!.setGraphHeight(this.options.fitViewModeHeight);

            // Does not work:
            // this.renderer!.reDraw(this.taxonomyEditorStore.focus)

            // Change the focus back
            const focus = this.taxonomyEditorStore.focus || this.renderer!.root;
            const factor = TaxonomyRenderer.estDurationFactor(this.renderer!.root, focus)
            const transition = this.renderer!.animateTarget(focus, factor)

            return this.renderer!.scrollWrapperToTop(transition)
                .end()
                .then(() => {
                    this.renderer!.setSvgViewPort(this.options.width, svgHeight);
                })
        } else {
            // The visualization is expanded
            const newHeight = this.renderer!.getFullViewModeHeight()

            // Update the total graph height of the visualization
            const svgHeight = this.renderer!.setGraphHeight(newHeight);

            // Update the view port of the SVG
            this.renderer!.setSvgViewPort(this.options.width, svgHeight)
            this.renderer!.setWrapperClasses(this.taxonomyEditorStore.builderMode)
            this.scrollWindowToBuilder();

            const focus = this.taxonomyEditorStore.focus || this.renderer!.root;
            const factor = TaxonomyRenderer.estDurationFactor(focus, this.renderer!.root)
            const transition = this.renderer!.animateTarget(focus, factor)
            const duration = transition.duration();

            return this.renderer!.scrollWrapperToFocus(duration, focus, newHeight);
        }
    }

    /**
     * Scroll the window to wrapper of the builder
     */
    private scrollWindowToBuilder() {
        const documentRect = document.body.getBoundingClientRect();
        const wrapperRect = this.wrapperRef.getBoundingClientRect();
        const buttonToolBarSize = 100;
        window.scrollTo({
            top: wrapperRect.top - documentRect.top - buttonToolBarSize,
            behavior: 'auto',
        })
    }

    isSelected(d: Node) {
        if (!d) {
            console.warn('Cannot determine selection of node', d)
            return false;
        }
        return this.taxonomyEditorStore.selection.findIndex(s => s.data.id === d.data.id) !== -1
    }

    isNavigateable(d: Node) {
        return TaxonomyTreeEditorController.isFocusNavClickable(d, this.taxonomyEditorStore.focus)
    }

    static isFocusNavClickable(node: Node, prevFocus: Node | null): boolean {
        return Boolean(TaxonomyTreeEditorController.focusNavClick(node, prevFocus));
    }

    /**
     * Get the new focus
     */
    private static focusNavClick(clickedNode: Node, prevFocus: Node | null): Node {
        if (!clickedNode.parent) {
            console.error('Expected user not to be able to click on the root')
            return clickedNode;
        }

        if (clickedNode.data.id === prevFocus?.data.id && clickedNode.parent) {
            // When the currently selected node is clicked
            // Go up one level
            return clickedNode.parent;
        } else if (clickedNode.height === 0) {
            // When the deepest node is clicked
            return clickedNode;

            // TODO: Do not limit the taxonomy size for now
            // // Limit the depth on which nodes can be added
            // // Go as far as we can
            // return clickedNode.parent;
        } else {
            return clickedNode;
        }
    }

    updateValueMode() {
        assignValues(this.taxonomyEditorStore.valueMode, this.renderer!.root);
        this.reDrawV2();
    }

    addNode(data: taxonomy_editor.AddNodeData): Node {
        // Build a new node

        const insert = this.getNewInsertPosition()
        console.log('Adding node', data, 'at', insert);
        // if (!insert) throw new Error('Cannot insert node at the parent');
        const [parent, index] = insert;

        const newNodeData = this.createNewNode(data)
        if (!parent.children) {
            parent.children = []
        }
        const newNode = TaxonomyTreeEditorController.insertNewNode(newNodeData, 1, parent, index);

        TaxonomyTreeEditorController.updateDepthDown(newNode);
        TaxonomyTreeEditorController.updateHeightUp(newNode);
        assignValues(this.taxonomyEditorStore.valueMode, parent);
        this.reDrawV2(true);

        return newNode;
    }

    private static insertNewNode(data: taxonomy_editor._NodeData, value: number, parent: Node, index: number): Node {
        if (!parent.children) throw new Error('Cannot insert new node')
        const newHierarchy = d3.hierarchy(data)

        // The position is missing, but it will be set when the renderer re calculated the layout
        const newNode = newHierarchy as Node

        // Hack into d3.js to set the properties
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const _newNode = newNode as Writeable<Node> as any
        _newNode.depth = parent.depth + 1
        _newNode.height = 0
        _newNode.parent = parent
        _newNode.value = value
        _newNode.x0 = parent.x0
        _newNode.x1 = parent.x1

        if (index === -1) {
            // Just append
            parent.children.push(newNode)
            parent.data.children.push(newNode.data)
        } else {
            parent.children.splice(index, 0, newNode)
            parent.data.children.splice(index, 0, newNode.data)
        }
        return newNode;
    }

    private getNewInsertPosition(): [Node, number] {
        const focus = this.taxonomyEditorStore.focus;
        if (focus && focus.depth !== 0) {
            // // Option1: Try to insert next to the focus
            // const parent = focus.parent as Node
            // if(!parent.children) {
            //     console.warn('No children')
            //     return
            // }
            // const i = parent.children.indexOf(focus)
            // return [parent, i]

            // Try to insert below to the focus
            return [focus, -1]
        } else {
            // Try to insert at the highest level
            const parent = this.renderer!.root
            return [parent, -1]
        }
    }

    mergeNodes(data: taxonomy_editor.MergeNodeData) {
        // Option1: Merge by creating a new node and moving everything to that node

        // // Optional: Change the order beforehand
        // const selection: taxonomy_editor.Node[] = r
        // const parentChildren = selection[0].parent?.children as Node[];
        // const i_s = selection.map(s => [
        //     s,
        //     parentChildren.findIndex(c => s.id === c.id)
        // ] as const)
        // i_s.sort(([_, a], [__, b]) => a - b)
        // const orderedSelection = i_s.map(([s,]) => s)

        // Note: destination.value is ignored for now...
        const destination = data.destination;
        const sources = data.sources;
        console.debug('Ignoring destination.value=', destination.value)

        const mainSubject = sources[0]
        const parent = mainSubject.parent as Node
        console.log('Merging into', mainSubject)
        // const otherSubjects = sources.slice(1)
        const parentChildren = parent.children;
        if (!parentChildren) return // should not happen
        const mainSubjectI = parentChildren.indexOf(mainSubject)

        const newNodeData = this.createMergedNode(data)
        const newValue = sum(sources.map(n => n.value || 0))
        const newNode = TaxonomyTreeEditorController.insertNewNode(newNodeData, newValue, parent, mainSubjectI);
        newNode.children = []

        for (const source of sources) {
            const srcParent = source.parent;
            if(!srcParent) throw new Error('Cannot merge the taxonomy root')
            if(!srcParent.children) throw new Error('Cannot merge a non-existing node')

            if (source.children) {
                // Move the children of this source to the new node, also the removed nodes
                newNode.data.children.push(...source.data.children)
                newNode.children.push(...source.children)
                source.children.forEach(c => c.parent = newNode)
            }

            const sPc = srcParent.children
            const sPd = srcParent.data
            // Remove this source from its own parent
            const sourceI = sPc.indexOf(source)
            console.assert(sourceI >= 0)
            sPc.splice(sourceI, 1)
            sPd.children.splice(sourceI, 1)

            // After the changes are applied, do the bookkeeping
            TaxonomyTreeEditorController.updateHeightUp(srcParent);
        }

        TaxonomyTreeEditorController.updateHeightUp(newNode)
        TaxonomyTreeEditorController.updateDepthDown(newNode);

        assignValues(this.taxonomyEditorStore.valueMode, parent)

        // Change the focus if needed
        const focusId = this.taxonomyEditorStore.focus?.data.id;
        if(focusId) {
            const forbiddenNodes = sources.flatMap(s => s.descendants());
            if(forbiddenNodes.find(n => n.data.id === focusId)) {
                const newFocus = parent ?? this.renderer!.root;
                this.taxonomyEditorStore.setFocus(newFocus);
            }
        }
        this.reDrawV2(true);
    }

    moveNodes(sources: Node[], parentDestinationSpec: Category): Node {
        const dstParent = this.findNodeById(parentDestinationSpec.node_id)
        if (!dstParent) {
            throw new Error('Cannot move nodes, destination not found: ' + parentDestinationSpec.node_id)
        }
        if (!dstParent.children) {
            dstParent.children = []
        }

        for (const source of sources) {
            const srcParent = source.parent;
            if (!srcParent) {
                throw new Error('Cannot move the taxonomy root')
            }

            const srcParentChildren = srcParent.children
            const srcParentData = srcParent.data
            if (!srcParentChildren || !srcParentData) {
                // Should not happen
                continue
            }
            // Remove this source from its own parent
            const sourceI = srcParentChildren.indexOf(source)
            console.assert(sourceI >= 0)
            srcParentChildren.splice(sourceI, 1)

            // Remove the source from the parent data as well
            // For safety we check if the index is the same, before we remove
            const sourceDI = srcParentData.children.findIndex(d => d.id === source.data.id)
            console.assert(sourceI === sourceDI)
            srcParentData.children.splice(sourceI, 1)

            // Add the source to the new parent
            dstParent.children.push(source)
            dstParent.data.children.push(source.data)
            source.parent = dstParent

            // After the changes are applied, do the bookkeeping
            TaxonomyTreeEditorController.updateDepthDown(source);
            TaxonomyTreeEditorController.updateHeightUp(srcParent);
            m_taxonomy.addAuthor(source.data.values, this.authStore.getUserId());
        }

        TaxonomyTreeEditorController.updateHeightUp(dstParent)

        assignValues(this.taxonomyEditorStore.valueMode, dstParent)
        this.reDrawV2();

        return dstParent
    }

    deleteSelection() {
        this.deleteNodes(this.taxonomyEditorStore.selection)
    }

    deleteNodes(selection: Node[]) {
        const parents: Node[] = []
        for (const source of selection) {
            const parent = source.parent;
            if (!parent) {
                throw new Error('Cannot remove the root of the tree!')
            }
            parents.push(parent)

            const sPc = parent.children
            const sPd = parent.data
            if (!sPc || !sPd) {
                console.error('Cannot delete, parent not found', source.data.id) // Should not happen
                continue
            }

            // Denote as deleted
            source.descendants().forEach(n => {
                m_taxonomy.addAuthor(n.data.values, this.authStore.getUserId());
                n.data.removed = true;
            })
        }

        // Do the bookkeeping
        parents.forEach(n => {
            TaxonomyTreeEditorController.updateHeightUp(n);
            // TaxonomyTreeEditorController.updateDepthDown(n); // not needed
        })
        assignValues(this.taxonomyEditorStore.valueMode, parents[0], ...parents.slice(1));

        // Change the focus if needed
        const focusId = this.taxonomyEditorStore.focus?.data.id;
        if(focusId) {
            const focusInSelection = selection.find(n => n.data.id === focusId);
            if(focusInSelection) {
                const newFocus = focusInSelection.parent ?? this.renderer!.root;
                this.taxonomyEditorStore.setFocus(newFocus);
            }
        }
        this.reDrawV2(true);
    }

    updateNodeFields(node: Node, name: string, description: string) {
        console.log('[UPDATE] updateNodeFields', node.data.id, name, description)
        node.data.label = name
        node.data.values.description = description
        m_taxonomy.addAuthor(node.data.values, this.authStore.getUserId());

        if (IS_TE_DEVELOPMENT_MODE) {
            // TODO: Cleanup for production code
            const lookupNode = this.findNodeById(node.data.id)
            if (node !== lookupNode) {
                console.error('Update a node which has been re-drawn already, please update the selection when changing the root of the renderer')
            }
            // lookupNode.data.label = name
            // lookupNode.data.values.description = description
            // const lookups = this.renderer!.root.descendants().filter(n => n.data.id === node.data.id)
            // const updateNode = node;
            // console.log('[UPDATE] updateNode =', updateNode?.data.id, updateNode?.data, updateNode?.data.label)
            // const node00 = this.renderer!.root.children?.at(0)?.children?.at(0);
            // console.log('[UPDATE] root[0,0] =', node00?.data.id, node00?.data.label)
            // console.log('[UPDATE] lookups =', lookups.map(n => [n.data.id, n.data.label]))
        }

        // TODO: Can be optimized as only the text needs to change, but that is not data-driven architecture...
        this.reDrawV2(true);
    }

    public static getLabels(node: Node): string[] {
        return node.ancestors().reverse().map(a => a.data.label)
        // .slice(1)
    }

    exportTree(): m_taxonomy.Tree {
        return taxonomy_editor.exportTree(this.renderer!.root)
    }

    private static updateHeightUp(node: Node | null) {
        if (!node) return

        // calc new height
        let newHeight: number;
        const children = node.children ?? [];
        if (children.length === 0) {
            newHeight = 1
        } else {
            newHeight = Math.max(...children.map(c => c.height));
        }

        // assign new height
        if (newHeight !== node.height) {

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const _node = node as Writeable<typeof node> as any
            _node.height = newHeight

            TaxonomyTreeEditorController.updateHeightUp(node.parent)
        }
    }

    private static updateDepthDown(node: Node | null) {
        if (!node) return

        // calc new depth
        let newDepth: number;
        if (!node.parent) {
            newDepth = 0;
        } else {
            newDepth = node.parent.depth + 1;
        }

        // assign new depth
        if(node.depth !== newDepth) {

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const _node = node as Writeable<typeof node> as any
            _node.depth = newDepth;

            const children = node.children ?? [];
            children.forEach(child => TaxonomyTreeEditorController.updateDepthDown(child))
        }
    }

    public getDescendants(): Node[] {
        return this.renderer!.root.descendants() || [];
    }

    refreshSelection() {
        this.taxonomyEditorStore.selectionIds.replace(this.taxonomyEditorStore.selectionIds)
    }

    resetSelection() {
        this.taxonomyEditorStore.setAndDrawSelection([]);
    }

    /**
     * To ensure the correct traces we need to lookup the node in the original root
     * @param nodeId
     */
    public findNodeById(nodeId: number): Node | null {
        return this.renderer!.root.find(node => node.data.id === nodeId) ?? null;
    }

    public findNodes(nodeIds: number[]): Node[] {
        return nodeIds.map(id => this.renderer!.root.find(node => node.data.id === id)) as Node[]
    }

    get isFitViewMode() {
        return this.taxonomyEditorStore.isFitMode;
    }

    public findFocus() {
        const focus = this.taxonomyEditorStore.focus;
        if (!focus) return null;
        return this.findNodeById(focus.data.id)
    }

    public destroy() {
        this.renderer?.destroy();
        this.reactions.forEach(disposer => disposer())
        this.taxonomyEditorStore.registerController(null)
    }
}
